import React, { useContext, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/modern/components/ui/dialog';
import { Button } from '@/modern/components/ui/button';
import { Input } from '@/modern/components/ui/input';
import { Label } from '@/modern/components/ui/label';
import { DatePicker } from '@/modern/components/ui/date-picker';
import { Avatar, AvatarFallback } from '@/modern/components/ui/avatar';
import { Badge } from '@/modern/components/ui/badge';
import { Combobox } from '@/modern/components/ui/combobox';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import BryntumContext from '@/react/context/bryntumContext';
import { ALERT_TYPES, ReminderTypes } from '@/other/constants/OtherKeys';
import { RECURRING_INTERVALS } from '@/bryntum-lib/models/Reminder';
import { DateHelper } from '@bryntum/schedulerpro';
import { GRANTS } from '@/other/constants/PrivilegeTypes';
import { Clock } from 'lucide-react';
import { cn } from '@/modern/utils';
import { EntityTypeKey } from '@/other/constants/EntityManagerConfig';
import { getEntityIdField } from '../utils/types';

// ============================================================================
// TYPE DEFINITIONS & SCHEMA
// ============================================================================

const extractInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2);
};

const reminderSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  alertDate: z.date(),
  alertTime: z.string().min(1, 'Alert time is required'),
  recurringInterval: z.number(),
  repeatCount: z.number().min(1, 'Repeat count must be at least 1').optional(),
});

type ReminderFormData = z.infer<typeof reminderSchema>;

interface ReminderEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  reminderId?: string;
  entityId: string;
  entityType: EntityTypeKey;
  defaultFormValues?: Partial<{ alertDate: Date; description: string }>;
  className?: string;
  /** Called after a successful save (create or edit). */
  onSuccess?: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ReminderEditModal: React.FC<ReminderEditModalProps> = ({
  open,
  onOpenChange,
  mode,
  reminderId,
  entityId,
  entityType,
  defaultFormValues,
  className,
  onSuccess
}) => {
  const { project, user } = useContext(BryntumContext);
  const [isLoading, setIsLoading] = React.useState(false);
  const [notifyUsers, setNotifyUsers] = React.useState<string[]>([]);

  // Check permissions
  const hasPermission = user?.can(GRANTS.grant_three);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors }
  } = useForm<ReminderFormData>({
    resolver: zodResolver(reminderSchema),
    defaultValues: {
      description: '',
      alertDate: new Date(),
      alertTime: format(new Date(), 'HH:mm'),
      recurringInterval: 0, // Never
      repeatCount: 1,
    }
  });

  const recurringInterval = watch('recurringInterval');
  const isRecurring = recurringInterval !== 0;

  // Keep repeatCount consistent when toggling recurring on/off
  useEffect(() => {
    if (!open) return;

    if (recurringInterval === 0) {
      // UX: if user selects "Never", ensure repeatCount is reset.
      setValue('repeatCount', 1, { shouldValidate: true, shouldDirty: true });
    } else {
      // If switching back to recurring, ensure repeatCount is at least 1.
      const currentRepeatCount = getValues('repeatCount');
      if (!currentRepeatCount || currentRepeatCount < 1) {
        setValue('repeatCount', 1, { shouldValidate: true, shouldDirty: true });
      }
    }
  }, [recurringInterval, open, setValue, getValues]);

  // Load existing reminder data in edit mode
  useEffect(() => {
    if (open && mode === 'edit' && reminderId && project) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const reminderStore = (project as any).reminderStore;
      const reminder = reminderStore?.getById(reminderId);

      if (reminder) {
        const alertDate = new Date(reminder.alertDate);
        reset({
          description: reminder.description || '',
          alertDate: alertDate,
          alertTime: format(alertDate, 'HH:mm'),
          recurringInterval: reminder.recurringInterval || 0,
          repeatCount: reminder.repeatCount || 1,
        });
      }
    } else if (open && mode === 'create') {
      // Reset to defaults for create mode (use defaultFormValues when provided, e.g. from date extraction)
      const alertDate = defaultFormValues?.alertDate ? new Date(defaultFormValues.alertDate) : DateHelper.add(new Date(), 5, 'minute');
      reset({
        description: defaultFormValues?.description ?? '',
        alertDate,
        alertTime: format(alertDate, 'HH:mm'),
        recurringInterval: 0,
        repeatCount: 1,
      });
    }
  }, [open, mode, reminderId, project, reset, defaultFormValues]);

  // Fetch notify users for display
  useEffect(() => {
    if (open && project && entityId) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const projectAny = project as any;
      let record = null;

      // Get the correct store based on entity type
      if (entityType === 'task') {
        record = projectAny.extraTaskStore?.getById(entityId);
      } else if (entityType === 'client') {
        record = projectAny.clientStore?.getById(entityId);
      } else if (entityType === 'resource') {
        record = projectAny.resourceStore?.getById(entityId);
      }

      // Only tasks have reminderNotifyUsers
      if (record && 'reminderNotifyUsers' in record && record.reminderNotifyUsers) {
        setNotifyUsers(record.reminderNotifyUsers);
      } else {
        setNotifyUsers([]);
      }
    }
  }, [open, project, entityId, entityType]);

  const onSubmit = async (data: ReminderFormData) => {
    if (!project || !hasPermission) return;

    try {
      setIsLoading(true);

      // Combine date and time
      const [hours, minutes] = data.alertTime.split(':');
      const alertDateTime = new Date(data.alertDate);
      alertDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      // Validate that alert date/time is not in the past
      if (DateHelper.isBefore(alertDateTime, new Date())) {
        alert('Cannot set alert date/time in the past');
        setIsLoading(false);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const reminderStore = (project as any).reminderStore;

      if (mode === 'edit' && reminderId) {
        // Update existing reminder
        const reminder = reminderStore.getById(reminderId);
        if (reminder) {
          reminder.set({
            description: data.description,
            alertDate: alertDateTime.toISOString(),
            recurringInterval: data.recurringInterval,
            repeatCount: data.recurringInterval !== 0 ? data.repeatCount : 0,
          });
        }
      } else {
        // Create new reminder
        const lastItemOrder = reminderStore.last?.order;
        const currentItemOrder = lastItemOrder ? lastItemOrder + 1 : 0;

        // Get the correct entity field and reminder type based on entity type
        const entityIdField = getEntityIdField(entityType);
        const reminderType = entityType === 'task' ? ReminderTypes.task :
                             entityType === 'client' ? ReminderTypes.client :
                             ReminderTypes.resource;

        const reminderRecord = reminderStore.createRecord({
          [entityIdField]: entityId,
          entityType: reminderType,
          order: currentItemOrder,
          alertType: ALERT_TYPES.REMINDER,
          alertTag: 'Reminder',
          alertDate: alertDateTime.toISOString(),
          description: data.description,
          repeatCount: data.recurringInterval !== 0 ? data.repeatCount : RECURRING_INTERVALS[0].defaultCount,
          recurringInterval: data.recurringInterval,
          archived: false,
        });

        reminderRecord.project = project;
        reminderStore.add(reminderRecord);
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving reminder:', error);
      alert('Failed to save reminder. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  if (!hasPermission) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={cn('max-w-[400px]', className)} dialogOverlayClassName={className}>
          <DialogHeader>
            <DialogTitle>Permission Denied</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-4">
            You do not have permission to create or edit reminders.
          </p>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={cn('max-w-[600px] max-h-[85vh] flex flex-col p-0 shadow-none', className)} dialogOverlayClassName={className}>
        <DialogHeader className="px-6 py-6 flex-shrink-0 bg-background border-b">
          <DialogTitle className="text-base font-normal">
            {mode === 'create' ? 'Add Reminder' : 'Edit Reminder'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-6 space-y-6 flex-1" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
          {/* Reminder Details Section */}
          <div className="space-y-4">
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">Description *</Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Input
                  id="description"
                  placeholder="Enter reminder description"
                  autoComplete="off"
                  {...field}
                  className={errors.description ? 'border-destructive' : ''}
                />
              )}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description.message}</p>
            )}
          </div>

          {/* Alert Date & Time - Two columns */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Alert Date *</Label>
              <Controller
                name="alertDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select alert date"
                    error={errors.alertDate?.message}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="alertTime" className="text-sm font-medium">Alert Time *</Label>
              <Controller
                name="alertTime"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      id="alertTime"
                      type="time"
                      autoComplete="off"
                      {...field}
                      className={`pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:cursor-pointer ${errors.alertTime ? 'border-destructive' : ''}`}
                    />
                  </div>
                )}
              />
              {errors.alertTime && (
                <p className="text-sm text-destructive">{errors.alertTime.message}</p>
              )}
            </div>
          </div>

          {/* Recurring Interval & Repeat Count - Two columns */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="recurringInterval" className="text-sm font-medium">Recurring</Label>
              <Controller
                name="recurringInterval"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={RECURRING_INTERVALS.map((interval) => ({
                      label: interval.value,
                      value: interval.id,
                    }))}
                    value={field.value}
                    onValueChange={(value) => field.onChange(value as number)}
                    placeholder="Select interval"
                    showSearch={false}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="repeatCount" className="text-sm font-medium">Repeat Count *</Label>
              <Controller
                name="repeatCount"
                control={control}
                render={({ field }) => (
                  <Input
                    id="repeatCount"
                    type="number"
                    min="1"
                    autoComplete="off"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    disabled={!isRecurring}
                    className={`${errors.repeatCount ? 'border-destructive' : ''} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                  />
                )}
              />
              {errors.repeatCount && (
                <p className="text-sm text-destructive">{errors.repeatCount.message}</p>
              )}
            </div>
          </div>

          </div>

          {/* Notify Users (Read-only) */}
          {notifyUsers.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-normal text-foreground">
                  Notify Users
                </h4>
                <Badge variant="secondary" className="h-5 px-2 text-xs">
                  {notifyUsers.length}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Users to be notified are managed via the Ownership tab.
              </p>
              <div className="flex flex-wrap gap-y-4 gap-x-3">
                {notifyUsers.map((user, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                        {extractInitials(user)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-foreground">{user}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>

          <DialogFooter className="gap-2 px-6 py-4 flex-shrink-0 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReminderEditModal;
